import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface ExplanationBlock {
  type: 'text' | 'table' | 'figure';
  content: string;
  headers?: string[];
  rows?: string[][];
  imageUrl?: string;
}

export interface Card {
  id: number;
  questionText: string;
  imageUrl: string;
  answerText: string;
  answerImageUrl: string;
  explanationText: string;
  explanationImageUrl: string;
  collapsed?: boolean;
  // Hindi version properties
  hindiQuestionText?: string;
  hindiImageUrl?: string;
  hindiAnswerText?: string;
  hindiAnswerImageUrl?: string;
  hindiExplanationText?: string;
  hindiExplanationImageUrl?: string;
  hindiQuestionImageUrl?: string;
  // Additional properties from API
  topicId?: string;
  yearIAS?: string;
  yearUPPSC?: string;
  yearBPSC?: string;
  yearMPPSC?: string;
  yearRPSC?: string;
  yearUKPSC?: string;
  yearCGPSC?: string;
  yearJPSC?: string;
  questionNumber?: number;
  // Option image URLs
  optionAImageUrl?: string;
  optionBImageUrl?: string;
  optionCImageUrl?: string;
  optionDImageUrl?: string;
  // Hindi option image URLs
  hindiOptionAImageUrl?: string;
  hindiOptionBImageUrl?: string;
  hindiOptionCImageUrl?: string;
  hindiOptionDImageUrl?: string;
  // Text-based options (OCR questions)
  optionAText?: string;
  optionBText?: string;
  optionCText?: string;
  optionDText?: string;
  // Hindi text-based options (bilingual)
  hindiOptionAText?: string;
  hindiOptionBText?: string;
  hindiOptionCText?: string;
  hindiOptionDText?: string;
  // Missing-side flags (bilingual alignment)
  missingHindi?: boolean;
  missingEnglish?: boolean;
  // Row identifier from bilingual JSON
  rowId?: string;
  // Question figure (from question_blocks)
  questionFigureUrl?: string;
  questionFigureCaption?: string;
  // Structured explanation blocks (OCR)
  explanationBlocks?: ExplanationBlock[];
  // Exam label e.g. "I.A.S. (Pre) 2003"
  examLabel?: string;
}

// New interfaces for the topic structure API responses
export interface Subject {
  subject_id: number;
  subject_name: string;
  subject_name_hindi?: string;
}

export interface Level1Topic {
  topic_id: string;
  topic_name: string;
  parent_topic_id: string;
  item_level: number;
}

export interface Level2Topic {
  topic_id: string;
  topic_name: string;
  parent_topic_id: string;
  item_level: number;
}

export interface Level3Topic {
  topic_id: string;
  topic_name: string;
  parent_topic_id: string;
  item_level: number;
}

// Common topic interface for new API response
export interface TopicItem {
  topic_id: string;
  name_eng: string;
  name_hindi?: string;
  parent_topic_id: string;
  item_level: number;
}

// New unified API response interface
export interface TopicsResponse {
  parent_topic_id: string;
  current_level: number;
  next_level: number;
  children: TopicItem[];
  has_children: boolean;
  count: number;
}

export interface Level1Response {
  parent_topic_id: string;
  level1_topics: Level1Topic[];
  count: number;
}

export interface Level2Response {
  parent_topic_id: string;
  level2_topics: Level2Topic[];
  count: number;
}

export interface Level3Response {
  parent_topic_id: string;
  level3_topics: Level3Topic[];
  count: number;
}

// New interface for subjects from zero-level API
export interface SubjectZeroResponse {
  topics: TopicItem[];
  count: number;
}

// Interface for topic tree node in the new API response
export interface TopicTreeNode {
  topic_id: string;
  name_eng: string;
  name_hindi?: string;
  parent_topic_id: string;
  item_level: number;
  depth: number;
  children: TopicTreeNode[];
}

// Interface for topic tree response from new API endpoint
export interface TopicTreeResponse {
  topic_id: string;
  tree: TopicTreeNode;
}

// New interface for yearwise exam data
export interface YearwiseResponse {
  exams: Exam[];
  total_exams: number;
  total_records: number;
}

// New interface for exam data
export interface Exam {
  exam_name: string;
  papers: Paper[];
}

// New interface for paper data with years
export interface Paper {
  paper_name: string;
  years: Year[];
}

// New interface for year data
export interface Year {
  id: number;
  year: number;
}

// Interface for batch topic trees API response
export interface BatchTopicTreesResponse {
  topic_count: number;
  trees: {
    [key: string]: TopicTreeNode;
  };
}

// ── Hierarchy API Interfaces ──

export interface HierarchyQuestionData {
  question_number: number;
  global_number: number;
  page: string;
  ques_img: string;
  ques_txt: string;
  ans_img: string;
  ans_txt: string;
  expl_img: string;
  expl_txt: string;
  file_path?: string;
  image_file?: string;
  // Option image URLs
  option_a_img?: string;
  option_b_img?: string;
  option_c_img?: string;
  option_d_img?: string;
}

export interface HierarchySubtopicData {
  name: string;
  name_eng?: string;
  name_hindi?: string;
  topic_id?: string;
  marker?: string;
  questions: HierarchyQuestionData[];
  hindi_questions?: HierarchyQuestionData[];
  subtopics?: HierarchySubtopicData[];
  first_seen_page?: string;
}

export interface HierarchyTopicData {
  name: string;
  name_eng?: string;
  name_hindi?: string;
  topic_id?: string;
  marker?: string;
  topic_number?: number;
  subtopics: HierarchySubtopicData[];
  questions: HierarchyQuestionData[];
  hindi_questions?: HierarchyQuestionData[];
  first_seen_page?: string;
}

export interface HierarchySectionData {
  name: string;
  name_eng?: string;
  name_hindi?: string;
  topic_id?: string;
  title: string;
  numeral?: string;
  topics: HierarchyTopicData[];
  direct_questions?: HierarchyQuestionData[];
  orphan_subtopics?: HierarchySubtopicData[];
  first_seen_page?: string;
}

export interface HierarchyData {
  subject: string;
  subject_topic_id: string;
  language: string;
  sections: HierarchySectionData[];
  orphan_topics: HierarchyTopicData[];
  total_questions: number;
  total_topics: number;
  total_subtopics: number;
  total_sections: number;
}

export interface HierarchyResponse {
  success: boolean;
  id: number;
  subject: string;
  language: string;
  data: HierarchyData;
  metadata: any;
}

export interface HierarchySubjectInfo {
  subject: string;
  language: string;
  file_count: number;
  total_questions: number;
  total_topics: number;
  total_sections: number;
  created_at: string;
  updated_at: string;
}

export interface HierarchySubjectsResponse {
  success: boolean;
  count: number;
  subjects: HierarchySubjectInfo[];
}

// New interface for the complete topic structure API response
export interface CompleteTopicStructureResponse {
  success: boolean;
  topics: TopicItem[];
  trees: {
    [key: string]: TopicTreeNode;
  };
  count: number;
}

@Injectable({
  providedIn: 'root'
})
export class DataService {
  private apiUrl = `${environment.apiBaseUrl}/api/questions`; // Use API URL from environment
  private topicsApiUrl = `${environment.apiBaseUrl}/api`; // Base URL for topics API

  constructor(private http: HttpClient) { }

  // Get the API base URL
  getApiBaseUrl(): string {
    return environment.apiBaseUrl;
  }

  // Get all subjects from the new API
  getAllSubjects(): Observable<Subject[]> {
    return this.http.get<Subject[]>(`${this.topicsApiUrl}/subjects`).pipe(
      catchError(this._handleError<Subject[]>('getAllSubjects', []))
    );
  }

  // Get all subjects from the new zero-level API endpoint
  /**
   * @deprecated Use getCompleteTopicStructure() instead
   */
  getSubjectsFromZeroLevel(): Observable<SubjectZeroResponse> {
    console.warn('getSubjectsFromZeroLevel is deprecated. Use getCompleteTopicStructure instead.');
    return this.http.get<SubjectZeroResponse>(`${this.topicsApiUrl}/topics/subjects/zero`).pipe(
      catchError(this._handleError<SubjectZeroResponse>('getSubjectsFromZeroLevel', { topics: [], count: 0 }))
    );
  }

  // Get topics by parent topic ID and level using the new unified API
  getTopicsByLevel(topicId: string, level: number): Observable<TopicsResponse> {
    // No need to adjust level for DB anymore as item_level now matches between UI and DB
    return this.http.get<TopicsResponse>(`${this.topicsApiUrl}/topics/${topicId}/level/${level}`).pipe(
      map(response => {
        // No need to adjust levels in response anymore
        return response;
      }),
      catchError(this._handleError<TopicsResponse>('getTopicsByLevel', { 
        parent_topic_id: topicId, 
        current_level: level, 
        next_level: level + 1, 
        children: [], 
        has_children: false, 
        count: 0 
      }))
    );
  }

  // DEPRECATED: Legacy methods for backwards compatibility - Use getTopicsByLevel instead
  getLevel1Topics(parentId: string): Observable<Level1Response> {
    console.warn('getLevel1Topics is deprecated. Use getTopicsByLevel instead.');
    return this.http.get<Level1Response>(`${this.topicsApiUrl}/topics/${parentId}/level1`).pipe(
      catchError(this._handleError<Level1Response>('getLevel1Topics', { parent_topic_id: parentId, level1_topics: [], count: 0 }))
    );
  }

  // DEPRECATED: Legacy method - Use getTopicsByLevel instead
  getLevel2Topics(parentId: string): Observable<Level2Response> {
    console.warn('getLevel2Topics is deprecated. Use getTopicsByLevel instead.');
    return this.http.get<Level2Response>(`${this.topicsApiUrl}/topics/${parentId}/level2`).pipe(
      catchError(this._handleError<Level2Response>('getLevel2Topics', { parent_topic_id: parentId, level2_topics: [], count: 0 }))
    );
  }

  // DEPRECATED: Legacy method - Use getTopicsByLevel instead
  getLevel3Topics(parentId: string): Observable<Level3Response> {
    console.warn('getLevel3Topics is deprecated. Use getTopicsByLevel instead.');
    return this.http.get<Level3Response>(`${this.topicsApiUrl}/topics/${parentId}/level3`).pipe(
      catchError(this._handleError<Level3Response>('getLevel3Topics', { parent_topic_id: parentId, level3_topics: [], count: 0 }))
    );
  }

  // Get all subjects
  getSubjects(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/subjects`).pipe(
      catchError(this._handleError<string[]>('getSubjects', []))
    );
  }

  // Get sections for a subject
  getSections(subject: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/subjects/${subject}/sections`).pipe(
      catchError(this._handleError<string[]>('getSections', []))
    );
  }

  // Get topics for a section
  getTopics(section: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/sections/${section}/topics`).pipe(
      catchError(this._handleError<string[]>('getTopics', []))
    );
  }

  // Get subtopics for a topic
  getSubtopics(topic: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/topics/${topic}/subtopics`).pipe(
      catchError(this._handleError<string[]>('getSubtopics', []))
    );
  }

  // Get questions for a subtopic
  getQuestions(subtopic: string): Observable<Card[]> {
    console.log(`Fetching questions from API for subtopic: ${subtopic}`);
    console.log(`API URL: ${this.apiUrl}`);

    // Use the API endpoint without the subtopic parameter since it's not supported yet
    return this.http.get<any>(`${this.apiUrl}`)
      .pipe(
        map(response => {
          console.log('API Response:', response);
          console.log(`API returned ${Array.isArray(response) ? response.length : 0} items`);

          // Map the API response to our Card interface
          const mappedData = Array.isArray(response) ? response.map((item: any) => {
            // Fix Google Cloud Storage URLs if needed
            const fixUrl = (url: string) => {
              if (!url) return '';
              
              if (url.includes('storage.cloud.google.com') || url.includes('storage.googleapis.com')) {
                // Extract just the path part from the GCS URL
                const urlObj = new URL(url);
                const pathParts = urlObj.pathname.split('/');
                
                // Remove empty first element if it exists
                if (pathParts[0] === '') pathParts.shift();
                
                // Remove the bucket name (first segment) as the API already knows which bucket to use
                if (pathParts.length > 0) pathParts.shift();
                
                // Join remaining parts to create the object path without the bucket name
                const objectPath = pathParts.join('/');
                
                // Return the URL with the API base URL and prefix
                return `${environment.apiBaseUrl}/api/gcs-files/${objectPath}`;
              }
              return url;
            };

            // Handle text fields that might be URLs
            const getTextContent = (textField: string) => {
              if (!textField) return 'No content available';

              // Return the actual text content
              return textField;
            };

            // Map the new API response fields to our Card interface
            return {
              id: item.ques_id || Math.random(),
              // English fields
              questionText: getTextContent(item.ques_txt_eng),
              imageUrl: fixUrl(item.ques_img_eng) || 'assets/mock-question-image.png',
              answerText: getTextContent(item.ans_txt_eng),
              answerImageUrl: fixUrl(item.ans_img_eng) || 'assets/mock-answer-image.png',
              explanationText: getTextContent(item.expl_txt_eng),
              explanationImageUrl: fixUrl(item.expl_img_eng) || 'assets/mock-expl-image.png',
              collapsed: false,
              // Hindi fields
              hindiQuestionText: getTextContent(item.ques_txt_hindi) || getTextContent(item.ques_txt_eng),
              hindiImageUrl: fixUrl(item.ques_img_hindi) || fixUrl(item.ques_img_eng) || 'assets/mock-question-image.png',
              hindiAnswerText: getTextContent(item.ans_txt_hindi) || getTextContent(item.ans_txt_eng),
              hindiAnswerImageUrl: fixUrl(item.ans_img_hindi) || fixUrl(item.ans_img_eng) || 'assets/mock-answer-image.png',
              hindiExplanationText: getTextContent(item.expl_txt_hindi) || getTextContent(item.expl_txt_eng),
              hindiExplanationImageUrl: fixUrl(item.expl_img_hindi) || fixUrl(item.expl_img_eng) || 'assets/mock-expl-image.png',
              hindiQuestionImageUrl: fixUrl(item.ques_img_hindi) || fixUrl(item.ques_img_eng) || 'assets/mock-question-image.png',
              // Additional properties from API
              topicId: item.topic_id,
              yearIAS: item.year_ias,
              yearUPPSC: item.year_uppsc,
              yearBPSC: item.year_bpsc,
              yearMPPSC: item.year_mppsc,
              yearRPSC: item.year_rpsc,
              yearUKPSC: item.year_ukpsc,
              yearCGPSC: item.year_cgpsc,
              yearJPSC: item.year_jpsc,
              questionNumber: item.question_number
            };
          }) : [];

          console.log(`Mapped ${mappedData.length} questions from API data`);
          
          return mappedData;
        }),
        catchError((error) => {
          console.error('API Error:', error);
          return this._handleError<Card[]>('getQuestions', [])(error);
        })
      );
  }

  // Get questions by topic ID (with optional topic name for hierarchy_nodes fallback)
  getQuestionsByTopicId(topicId: string, topicName?: string): Observable<Card[]> {
    let url = `${this.topicsApiUrl}/questions/bytopic/${topicId}`;
    if (topicName) {
      url += `?name=${encodeURIComponent(topicName)}`;
    }
    console.log(`Fetching questions for topic ID: ${topicId}${topicName ? ` (name: ${topicName})` : ''}`);
    return this.http.get<any>(url)
      .pipe(
        map(response => {
          console.log('API Response for questions by topic ID:', response);
          
          if (!response || !response.questions || !Array.isArray(response.questions)) {
            console.warn('No questions found or invalid response format for topic ID:', topicId);
            return [];
          }
          
          // Map the API response to our Card interface
          const mappedData = response.questions.map((item: any) => {
            // Fix Google Cloud Storage URLs if needed
            const fixUrl = (url: string) => {
              if (!url) return '';
              
              if (url.includes('storage.cloud.google.com') || url.includes('storage.googleapis.com')) {
                // Extract just the path part from the GCS URL
                const urlObj = new URL(url);
                const pathParts = urlObj.pathname.split('/');
                
                // Remove empty first element if it exists
                if (pathParts[0] === '') pathParts.shift();
                
                // Remove the bucket name (first segment) as the API already knows which bucket to use
                if (pathParts.length > 0) pathParts.shift();
                
                // Join remaining parts to create the object path without the bucket name
                const objectPath = pathParts.join('/');
                
                // Return the URL with the API base URL and prefix
                return `${environment.apiBaseUrl}/api/gcs-files/${objectPath}`;
              }
              return url;
            };

            // Handle text fields that might be URLs
            const getTextContent = (textField: string) => {
              if (!textField) return 'No content available';
              // Return the actual text content
              return textField;
            };

            // Map the API response fields to our Card interface
            return {
              id: item.ques_id || Math.random(),
              // English fields
              questionText: getTextContent(item.ques_txt_eng),
              imageUrl: fixUrl(item.ques_img_eng) || 'assets/mock-question-image.png',
              answerText: getTextContent(item.ans_txt_eng),
              answerImageUrl: fixUrl(item.ans_img_eng) || 'assets/mock-answer-image.png',
              explanationText: getTextContent(item.expl_txt_eng),
              explanationImageUrl: fixUrl(item.expl_img_eng) || 'assets/mock-expl-image.png',
              collapsed: false,
              // Hindi fields
              hindiQuestionText: getTextContent(item.ques_txt_hindi) || getTextContent(item.ques_txt_eng),
              hindiImageUrl: fixUrl(item.ques_img_hindi) || fixUrl(item.ques_img_eng) || 'assets/mock-question-image.png',
              hindiAnswerText: getTextContent(item.ans_txt_hindi) || getTextContent(item.ans_txt_eng),
              hindiAnswerImageUrl: fixUrl(item.ans_img_hindi) || fixUrl(item.ans_img_eng) || 'assets/mock-answer-image.png',
              hindiExplanationText: getTextContent(item.expl_txt_hindi) || getTextContent(item.expl_txt_eng),
              hindiExplanationImageUrl: fixUrl(item.expl_img_hindi) || fixUrl(item.expl_img_eng) || 'assets/mock-expl-image.png',
              hindiQuestionImageUrl: fixUrl(item.ques_img_hindi) || fixUrl(item.ques_img_eng) || 'assets/mock-question-image.png',
              // Additional properties from API
              topicId: item.topic_id,
              yearIAS: item.year_ias,
              yearUPPSC: item.year_uppsc,
              yearBPSC: item.year_bpsc,
              yearMPPSC: item.year_mppsc,
              yearRPSC: item.year_rpsc,
              yearUKPSC: item.year_ukpsc,
              yearCGPSC: item.year_cgpsc,
              yearJPSC: item.year_jpsc,
              questionNumber: item.question_number
            };
          });
          
          console.log(`Mapped ${mappedData.length} questions from topic ID: ${topicId}`);
          return mappedData;
        }),
        catchError(error => {
          console.error(`Error fetching questions for topic ID ${topicId}:`, error);
          return this._handleError<Card[]>('getQuestionsByTopicId', [])(error);
        })
      );
  }

  // Get the complete topic structure using the new unified API endpoint
  getCompleteTopicStructure(): Observable<CompleteTopicStructureResponse> {
    console.log('Calling API to get complete topic structure');
    console.log('⚡ SENDING HTTP REQUEST to /topics/complete-structure');
    // Remove caching logic, directly return API call
    return this.http.get<CompleteTopicStructureResponse>(`${this.topicsApiUrl}/topics/complete-structure`).pipe(
      map(response => {
        console.log('⚡ API RESPONSE RECEIVED from /topics/complete-structure');
        console.log('Complete topic structure API response status:', response?.success);
        console.log('Topics count:', response?.topics?.length);
        console.log('Trees count:', Object.keys(response?.trees || {}).length);
        
        // Validate response structure
        if (!response.success) {
          console.error('API returned success: false for complete topic structure');
        }
        if (!response.topics || !Array.isArray(response.topics) || response.topics.length === 0) {
          console.error('API returned empty or invalid topics array');
        }
        if (!response.trees || Object.keys(response.trees).length === 0) {
          console.error('API returned empty or invalid trees object');
        }
        
        // Create a safe copy of the response with default empty values for anything missing
        const safeResponse: CompleteTopicStructureResponse = {
          success: response?.success || false,
          topics: Array.isArray(response?.topics) ? response.topics : [],
          trees: response?.trees || {},
          count: response?.count || 0
        };
        
        return safeResponse;
      }),
      catchError(error => {
        console.error('Error fetching complete topic structure:', error);
        return this._handleError<CompleteTopicStructureResponse>('getCompleteTopicStructure', { 
          success: false,
          topics: [],
          trees: {},
          count: 0
        })(error);
      })
    );
  }

  // Get topic tree without caching
  getTopicTree(topicId: string): Observable<TopicTreeResponse> {
    return this.http.get<TopicTreeResponse>(`${this.topicsApiUrl}/topics/${topicId}/tree`).pipe(
      catchError(this._handleError<TopicTreeResponse>('getTopicTree', { 
        topic_id: topicId, 
        tree: {
          topic_id: topicId,
          name_eng: 'Error',
          parent_topic_id: '0',
          item_level: 0,
          depth: 0,
          children: []
        }
      }))
    );
  }

  // Get yearwise exam data
  getYearwiseData(): Observable<YearwiseResponse> {
    return this.http.get<YearwiseResponse>(`${environment.apiBaseUrl}/api/yearwise`).pipe(
      catchError(this._handleError<YearwiseResponse>('getYearwiseData', { exams: [], total_exams: 0, total_records: 0 }))
    );
  }

  // ── Hierarchy API Methods ──

  /**
   * Get list of all subjects that have hierarchy data stored in the database.
   * Uses the new /api/hierarchy/subjects endpoint.
   */
  getHierarchySubjects(): Observable<HierarchySubjectsResponse> {
    console.log('Fetching subjects from hierarchy API...');
    return this.http.get<HierarchySubjectsResponse>(`${this.topicsApiUrl}/hierarchy/subjects`).pipe(
      catchError(this._handleError<HierarchySubjectsResponse>('getHierarchySubjects', {
        success: false, count: 0, subjects: []
      }))
    );
  }

  /**
   * Get full hierarchy data for a subject (sections → topics → subtopics → questions).
   * Uses the new /api/hierarchy/subject/:subject endpoint.
   * Returns the complete tree with all questions and their 6-field data.
   */
  getHierarchyBySubject(subject: string, language: string = 'english'): Observable<HierarchyResponse> {
    console.log(`Fetching hierarchy for subject: ${subject}, language: ${language}`);
    const encodedSubject = encodeURIComponent(subject);
    return this.http.get<HierarchyResponse>(
      `${this.topicsApiUrl}/hierarchy/subject/${encodedSubject}?language=${language}`
    ).pipe(
      catchError(this._handleError<HierarchyResponse>('getHierarchyBySubject', {
        success: false, id: 0, subject, language, data: {
          subject, subject_topic_id: '', language,
          sections: [], orphan_topics: [],
          total_questions: 0, total_topics: 0, total_subtopics: 0, total_sections: 0
        }, metadata: null
      }))
    );
  }

  /**
   * Convert a HierarchyQuestionData (from hierarchy API) into the standard Card interface.
   * Handles GCS URL proxying through the backend for CORS-safe image loading.
   */
  convertHierarchyQuestionToCard(q: HierarchyQuestionData, language: string = 'english'): Card {
    const fixUrl = (url: string): string => {
      if (!url) return '';
      // Proxy GCS URLs through the backend to avoid CORS issues
      if (url.includes('storage.cloud.google.com') || url.includes('storage.googleapis.com')) {
        try {
          const urlObj = new URL(url);
          const pathParts = urlObj.pathname.split('/');
          if (pathParts[0] === '') pathParts.shift();
          if (pathParts.length > 0) pathParts.shift(); // remove bucket name
          const objectPath = pathParts.join('/');
          return `${environment.apiBaseUrl}/api/gcs-files/${objectPath}`;
        } catch {
          return url;
        }
      }
      return url;
    };

    const card: Card = {
      id: q.global_number || q.question_number,
      questionText: q.ques_txt || 'No content available',
      imageUrl: fixUrl(q.ques_img) || '',
      answerText: q.ans_txt || 'No content available',
      answerImageUrl: fixUrl(q.ans_img) || '',
      explanationText: q.expl_txt || 'No content available',
      explanationImageUrl: fixUrl(q.expl_img) || '',
      collapsed: false,
      questionNumber: q.question_number,
      // Option images
      optionAImageUrl: fixUrl(q.option_a_img || '') || '',
      optionBImageUrl: fixUrl(q.option_b_img || '') || '',
      optionCImageUrl: fixUrl(q.option_c_img || '') || '',
      optionDImageUrl: fixUrl(q.option_d_img || '') || '',
    };

    // For Hindi, copy same fields (hierarchy stores per-language data)
    if (language === 'hindi') {
      card.hindiQuestionText = q.ques_txt || '';
      card.hindiImageUrl = fixUrl(q.ques_img) || '';
      card.hindiAnswerText = q.ans_txt || '';
      card.hindiAnswerImageUrl = fixUrl(q.ans_img) || '';
      card.hindiExplanationText = q.expl_txt || '';
      card.hindiExplanationImageUrl = fixUrl(q.expl_img) || '';
      card.hindiOptionAImageUrl = fixUrl(q.option_a_img || '') || '';
      card.hindiOptionBImageUrl = fixUrl(q.option_b_img || '') || '';
      card.hindiOptionCImageUrl = fixUrl(q.option_c_img || '') || '';
      card.hindiOptionDImageUrl = fixUrl(q.option_d_img || '') || '';
    }

    return card;
  }

  // Error handling
  private _handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(`${operation} failed: ${error.message}`);
      // Let the app keep running by returning an empty result
      return of(result as T);
    };
  }
}
